import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL } from '../config/api';
import AdminNav from './AdminNav';
import './DraftReview.css';

// Draft review queue (2026-10-03). Heeyoon gets this one link instead of a
// message per draft: every video waiting for review — scheduler uploads and
// videos sent in Slack DMs — shows up here, oldest first.
//
// One draft fills the screen and the page only asks for a decision. The note is
// optional for both approve and "request changes" — feedback itself goes out in
// Slack and is already captured there (2026-10-06, 태준).
//
// Approve sends the creator a "go ahead and post" DM (plus the note, if any);
// "request changes" sends the note as a DM when one is written.
//
// Scene feedback (2026-10-27, 태준): pause the video anywhere and add a note for
// that moment. The backend grabs the frame and translates Korean to English, and
// the decision DM carries one link to a creator-facing report (/feedback/:token)
// with each frame, its timestamp and the note. Replies sent from Slack instead
// can paste the same link ("리포트 링크 복사"), then "이미 검토함".
//
// The password is checked by the backend (Authorization: Bearer), not here —
// the old in-page "reviewed" button was removed because anyone could press it.

const AUTH_KEY = 'aff-auth:draft-review';
const BASE = `${API_URL}/api/draft-review`;

interface Draft {
  id: string;
  source: 'scheduler' | 'slack';
  handle: string;
  route: string;
  instagram: string | null;
  country: string | null;
  stage: string;
  firstDraftReviewedAt: string | null;
  day: number | null;
  slot: number | null;
  version: number;
  versions: number;
  prevNote: string | null;
  playUrl: string | null;
  filename: string | null;
  sizeBytes: number | null;
  uploadedAt: string;
  hoursWaiting: number;
  scheduleUrl: string;
  slackUrl: string | null;
  aiReview: AiReview | null;
  timedNotes: TimedNote[];
  reportUrl: string | null;
}

// text = what the reviewer typed; textEn = what the creator reads (same when typed in English).
interface TimedNote {
  noteId: string;
  t: number;
  text: string;
  textEn: string;
  frameUrl: string | null;
  at: string;
}

// 🤖 AI first pass (2026-10-22, 태준): ugc-agent/aiReviewDrafts.js runs each queued
// video through Gemini against the guide and leaves a verdict, the reasons (Korean,
// for the reviewer) and a ready-to-send English note. The reviewer only agrees or
// disagrees; agreeing sends exactly what the AI proposed.
interface AiReview {
  verdict: 'approve' | 'revise';
  summaryKo: string;
  issuesKo: string[];
  noteEn: string;
  format: string;
  model: string;
  at: string;
}

type Decision = 'approve' | 'reject' | 'skip' | 'already';

const DECISION_LABEL: Record<Decision, string> = { approve: '✅ 승인', reject: '🔁 수정 요청', skip: '🚫 초안 아님', already: '💬 이미 검토함' };

const fmtWait = (h: number) => {
  if (h < 1) return '방금';
  if (h < 24) return `${h}시간째`;
  return `${Math.floor(h / 24)}일 ${h % 24}시간째`;
};
const fmtMB = (n: number | null) => (n == null ? null : `${(n / 1048576).toFixed(1)} MB`);
const fmtT = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toFixed(1).padStart(4, '0')}`;
const isTyping = (el: EventTarget | null) => el instanceof HTMLElement && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName);

const readPw = () => { try { return localStorage.getItem(AUTH_KEY) || ''; } catch { return ''; } };
const savePw = (pw: string) => { try { localStorage.setItem(AUTH_KEY, pw); } catch { /* storage disabled */ } };

const DraftReview: React.FC = () => {
  const [pw, setPw] = useState(readPw);
  const [pwInput, setPwInput] = useState('');
  const [authed, setAuthed] = useState(false);
  const [rows, setRows] = useState<Draft[]>([]);
  const [loading, setLoading] = useState(false);
  const [idx, setIdx] = useState(0);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [done, setDone] = useState<{ id: string; decision: Decision; who: string }[]>([]);
  // Scene feedback: vt follows the playhead; the composer saves at wherever the video is paused.
  const videoRef = useRef<HTMLVideoElement>(null);
  const [vt, setVt] = useState(0);
  const [composing, setComposing] = useState(false);
  const [sceneText, setSceneText] = useState('');
  const [sceneBusy, setSceneBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const headers = useCallback((p: string) => ({ Authorization: `Bearer ${p}`, 'Content-Type': 'application/json' }), []);

  const load = useCallback(async (p: string) => {
    if (!p) return;
    setLoading(true);
    try {
      const r = await fetch(BASE, { headers: headers(p) });
      if (r.status === 401) { setAuthed(false); setErrors((x) => [...x, '비밀번호가 맞지 않습니다']); return; }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setRows(j.rows || []);
      setIdx(0);
      if (!authed) setErrors([]);  // drop the 'wrong password' line once in
      setAuthed(true);
      savePw(p);
    } catch (e) {
      setErrors((x) => [...x, `목록을 못 불러왔습니다: ${(e as Error).message}`]);
    } finally {
      setLoading(false);
    }
  }, [headers, authed]);

  useEffect(() => { if (pw) load(pw); }, [pw]); // eslint-disable-line react-hooks/exhaustive-deps

  const cur = rows[idx] || null;

  // Clear the note when moving to another draft so feedback never carries over.
  // Keyed on the draft, not idx: after a decision the row is removed and idx often
  // stays put, so the next draft would inherit the note (10/31 ryan's note went out
  // with juan's and koen's approvals).
  useEffect(() => { setNote(''); setComposing(false); setSceneText(''); setCopied(false); setVt(0); }, [cur?.id]);

  const patchRow = useCallback((id: string, fn: (d: Draft) => Draft) => {
    setRows((rs) => rs.map((d) => (d.id === id ? fn(d) : d)));
  }, []);

  const openComposer = useCallback(() => {
    const v = videoRef.current;
    if (v) { v.pause(); setVt(v.currentTime); }
    setComposing(true);
  }, []);

  // N = add feedback at the current frame (outside text boxes).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== 'n' || e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target)) return;
      if (!cur || !cur.playUrl) return;
      e.preventDefault();
      openComposer();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cur, openComposer]);

  const addScene = useCallback(async () => {
    const text = sceneText.trim();
    if (!cur || sceneBusy || !text) return;
    const id = cur.id;
    const t = videoRef.current ? videoRef.current.currentTime : vt;
    setSceneBusy(true);
    try {
      const r = await fetch(`${BASE}/notes`, { method: 'POST', headers: headers(pw), body: JSON.stringify({ id, t, text }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      patchRow(id, (d) => ({ ...d, timedNotes: [...d.timedNotes, j.note as TimedNote].sort((a, b) => a.t - b.t) }));
      setSceneText('');
      setComposing(false);
    } catch (e) {
      setErrors((x) => [...x, `장면 피드백 저장 실패: ${(e as Error).message}`]);
    } finally {
      setSceneBusy(false);
    }
  }, [cur, sceneBusy, sceneText, vt, headers, pw, patchRow]);

  const removeScene = useCallback(async (noteId: string) => {
    if (!cur) return;
    const id = cur.id;
    try {
      const r = await fetch(`${BASE}/notes/delete`, { method: 'POST', headers: headers(pw), body: JSON.stringify({ id, noteId }) });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      patchRow(id, (d) => ({ ...d, timedNotes: d.timedNotes.filter((n) => n.noteId !== noteId) }));
    } catch (e) {
      setErrors((x) => [...x, `장면 피드백 삭제 실패: ${(e as Error).message}`]);
    }
  }, [cur, headers, pw, patchRow]);

  const seek = useCallback((t: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    v.currentTime = t;
    v.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, []);

  // Issues the report link (same link the decision DM would carry) and copies it, for replies sent from Slack.
  const copyReport = useCallback(async () => {
    if (!cur) return;
    const id = cur.id;
    try {
      const r = await fetch(`${BASE}/report`, { method: 'POST', headers: headers(pw), body: JSON.stringify({ id }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      patchRow(id, (d) => ({ ...d, reportUrl: j.reportUrl }));
      await navigator.clipboard.writeText(j.reportUrl).catch(() => {});
      setCopied(true);
    } catch (e) {
      setErrors((x) => [...x, `리포트 링크 발급 실패: ${(e as Error).message}`]);
    }
  }, [cur, headers, pw, patchRow]);

  // noteOverride: the AI's note when the reviewer agrees with it, so a half-typed
  // note in the box never goes out by accident.
  const decide = useCallback(async (decision: Decision, noteOverride?: string) => {
    if (!cur || busy) return;
    setBusy(true);
    const who = cur.instagram || cur.handle;
    try {
      const sendNote = (noteOverride ?? note).trim();
      const r = await fetch(`${BASE}/review`, {
        method: 'POST', headers: headers(pw), body: JSON.stringify({ id: cur.id, decision, note: sendNote })
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        throw new Error(j.error === 'note_must_be_english'
          ? '메모가 크리에이터에게 DM으로 갑니다 — 영어로 적어 주세요'
          : (j.error || `HTTP ${r.status}`));
      }
      if (j.dm && !j.dm.sent) {
        setErrors((x) => [...x, `${who}: 기록은 됐지만 DM 발송 실패(${j.dm.reason}) — Slack에서 직접 보내 주세요`]);
      }
      setDone((d) => [...d, { id: cur.id, decision, who }]);
      setRows((rs) => rs.filter((_, i) => i !== idx));
      setIdx((i) => Math.min(i, Math.max(0, rows.length - 2)));
    } catch (e) {
      setErrors((x) => [...x, `${who}: ${(e as Error).message}`]);
    } finally {
      setBusy(false);
    }
  }, [cur, busy, note, idx, rows.length, headers, pw]);

  const undo = useCallback(async (id: string) => {
    try {
      const r = await fetch(`${BASE}/unreview`, { method: 'POST', headers: headers(pw), body: JSON.stringify({ id }) });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setDone((d) => d.filter((x) => x.id !== id));
      await load(pw);
    } catch (e) {
      setErrors((x) => [...x, `되돌리기 실패: ${(e as Error).message}`]);
    }
  }, [headers, pw, load]);

  if (!authed) {
    return (
      <div className="drev-gate">
        <h1>Draft review</h1>
        <input
          type="password"
          placeholder="비밀번호"
          value={pwInput}
          onChange={(e) => setPwInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') setPw(pwInput); }}
        />
        <button className="drev-btn drev-primary" onClick={() => setPw(pwInput)} disabled={loading}>
          {loading ? '확인 중…' : '열기'}
        </button>
        {errors.length > 0 && <p className="drev-err">{errors[errors.length - 1]}</p>}
      </div>
    );
  }

  return (
    <div className="drev">
      <header className="drev-head">
        <div>
          <h1>Draft review</h1>
          <p className="drev-sub">
            {loading ? '불러오는 중…' : rows.length ? `검토 대기 ${rows.length}건` : '검토할 초안이 없습니다'}
          </p>
        </div>
        <div className="drev-head-actions">
          <AdminNav />
          <button className="drev-btn" onClick={() => load(pw)} disabled={loading}>새로고침</button>
        </div>
      </header>

      {errors.length > 0 && (
        <div className="drev-errors">
          {errors.map((e, i) => <p key={i}>⚠️ {e}</p>)}
          <button className="drev-btn drev-sm" onClick={() => setErrors([])}>지우기</button>
        </div>
      )}

      {done.length > 0 && (
        <div className="drev-done">
          {done.map((d) => (
            <span key={d.id}>
              {DECISION_LABEL[d.decision]} {d.who}
              <button className="drev-link" onClick={() => undo(d.id)}>되돌리기</button>
            </span>
          ))}
        </div>
      )}

      {!loading && !rows.length && (
        <div className="drev-empty">
          <p>검토할 초안이 없습니다.</p>
          <p className="drev-quiet">스케줄러 업로드는 바로, Slack DM 영상은 20분마다 여기로 모입니다.</p>
        </div>
      )}

      {cur && (
        <>
          <div className="drev-nav">
            <button className="drev-btn drev-sm" onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0}>← 이전</button>
            <span>{idx + 1} / {rows.length}</span>
            <button className="drev-btn drev-sm" onClick={() => setIdx((i) => Math.min(rows.length - 1, i + 1))} disabled={idx >= rows.length - 1}>다음 →</button>
          </div>

          <div className="drev-card">
            <div className="drev-who">
              <div>
                <h2>{cur.instagram ? `@${cur.instagram}` : cur.handle}</h2>
                <p className="drev-meta">
                  <code>{cur.handle}</code>
                  {cur.country && <> · {cur.country}</>}
                  {' · '}{cur.firstDraftReviewedAt ? '게시 중' : <b className="drev-first">첫 초안</b>}
                </p>
              </div>
              <div className="drev-wait"><b>{fmtWait(cur.hoursWaiting)}</b><span>대기</span></div>
            </div>

            <p className="drev-src">
              {cur.source === 'slack'
                ? 'Slack DM으로 받은 영상'
                : `스케줄러 Day ${cur.day} · ${cur.slot === 1 ? '5pm' : '9pm'} 칸`}
              {cur.versions > 1 && <> · 재업로드 v{cur.version}</>}
            </p>
            {cur.prevNote && <div className="drev-prev">지난 피드백 — {cur.prevNote}</div>}

            {cur.playUrl ? (
              <video
                key={cur.id}
                ref={videoRef}
                className="drev-video"
                src={cur.playUrl}
                controls
                playsInline
                preload="metadata"
                onTimeUpdate={(e) => setVt(e.currentTarget.currentTime)}
                onSeeked={(e) => setVt(e.currentTarget.currentTime)}
              />
            ) : (
              <div className="drev-novideo">영상 링크가 없습니다.</div>
            )}

            {cur.playUrl && !composing && (
              <div className="drev-scene-bar">
                <button className="drev-btn drev-sm drev-primary" onClick={openComposer}>＋ 이 장면에 피드백 · {fmtT(vt)}</button>
                <span className="drev-quiet">영상을 원하는 장면에 멈추고 누르세요 (단축키 N)</span>
              </div>
            )}

            {composing && (
              <div className="drev-composer">
                <div className="drev-composer-head">
                  <b>{fmtT(vt)}</b> 장면 <span className="drev-quiet">— 영상을 움직이면 시점이 따라갑니다</span>
                </div>
                <textarea
                  className="drev-input"
                  autoFocus
                  rows={2}
                  placeholder="이 장면에서 고칠 점 — 한국어로 적으면 영어로 옮겨서 전달됩니다"
                  value={sceneText}
                  onChange={(e) => setSceneText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); addScene(); }
                    if (e.key === 'Escape') { setComposing(false); }
                  }}
                />
                <div className="drev-actions">
                  <button className="drev-btn drev-sm drev-primary" onClick={addScene} disabled={sceneBusy || !sceneText.trim()}>
                    {sceneBusy ? '프레임 캡처·번역 중…' : '추가 (⌘↵)'}
                  </button>
                  <button className="drev-btn drev-sm" onClick={() => setComposing(false)} disabled={sceneBusy}>취소</button>
                </div>
              </div>
            )}

            {cur.timedNotes.length > 0 && (
              <div className="drev-scenes">
                <div className="drev-scenes-head">
                  <b>장면별 피드백 {cur.timedNotes.length}개</b>
                  <span className="drev-scenes-links">
                    <button className="drev-link" onClick={copyReport}>{copied ? '복사됨' : '리포트 링크 복사'}</button>
                    {cur.reportUrl && <a href={cur.reportUrl} target="_blank" rel="noopener noreferrer">크리에이터 화면 보기</a>}
                  </span>
                </div>
                <ol className="drev-scene-list">
                  {cur.timedNotes.map((n) => (
                    <li key={n.noteId} className="drev-scene">
                      <button className="drev-scene-frame" onClick={() => seek(n.t)} title="이 장면으로 이동">
                        {n.frameUrl && <img src={n.frameUrl} alt="" loading="lazy" />}
                        <span className="drev-scene-t">{fmtT(n.t)}</span>
                      </button>
                      <div className="drev-scene-body">
                        <p>{n.text}</p>
                        {n.textEn !== n.text && <p className="drev-scene-en">EN · {n.textEn}</p>}
                      </div>
                      <button className="drev-link drev-scene-del" onClick={() => removeScene(n.noteId)}>삭제</button>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <p className="drev-file">
              {cur.filename}{fmtMB(cur.sizeBytes) && <> · {fmtMB(cur.sizeBytes)}</>}
              {' · '}{new Date(cur.uploadedAt).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })}
              {cur.playUrl && <> · <a href={cur.playUrl} target="_blank" rel="noopener noreferrer">원본 열기</a></>}
            </p>

            <div className="drev-links">
              {cur.slackUrl && <a href={cur.slackUrl} target="_blank" rel="noopener noreferrer">Slack DM 열기</a>}
              <a href={cur.scheduleUrl} target="_blank" rel="noopener noreferrer">스케줄러 보기</a>
            </div>

            {cur.aiReview ? (
              <div className={`drev-ai ${cur.aiReview.verdict === 'approve' ? 'drev-ai-ok' : 'drev-ai-fix'}`}>
                <div className="drev-ai-head">
                  <b>🤖 AI 1차 검토 — {cur.aiReview.verdict === 'approve' ? '✅ 승인 권장' : '🔁 수정 권장'}</b>
                  {cur.aiReview.format && <span className="drev-quiet"> · {cur.aiReview.format}</span>}
                </div>
                {cur.aiReview.summaryKo && <p className="drev-ai-sum">{cur.aiReview.summaryKo}</p>}
                {cur.aiReview.issuesKo.length > 0 && (
                  <ul className="drev-ai-issues">
                    {cur.aiReview.issuesKo.map((x, i) => <li key={i}>{x}</li>)}
                  </ul>
                )}
                {cur.aiReview.noteEn && (
                  <div className="drev-ai-note">
                    <span className="drev-quiet">크리에이터에게 보낼 메시지(영어)</span>
                    <p>{cur.aiReview.noteEn}</p>
                  </div>
                )}
                <div className="drev-actions">
                  <button
                    className={`drev-btn ${cur.aiReview.verdict === 'approve' ? 'drev-primary' : 'drev-danger'}`}
                    onClick={() => decide(cur.aiReview!.verdict === 'approve' ? 'approve' : 'reject', cur.aiReview!.noteEn)}
                    disabled={busy}
                  >👍 동의 — {cur.aiReview.verdict === 'approve' ? '승인' : '수정 요청'} 보내기</button>
                  <button className="drev-btn" onClick={() => setNote(cur.aiReview!.noteEn)} disabled={busy || !cur.aiReview.noteEn}>✏️ 메시지 고쳐서 보내기</button>
                </div>
                <p className="drev-hint">부동의면 아래에서 직접 결론을 고르세요. 동의 여부는 AI 정확도 측정용으로 기록됩니다.</p>
              </div>
            ) : (
              <p className="drev-quiet">🤖 AI 1차 검토 중… (15분마다 갱신)</p>
            )}

            <textarea
              className="drev-input"
              placeholder="메모(선택, 영어) — 승인이면 덧붙일 말, 수정 요청이면 고칠 점. 적으면 그대로 크리에이터에게 DM으로 갑니다."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
            />

            <div className="drev-actions">
              <button className="drev-btn drev-primary" onClick={() => decide('approve')} disabled={busy}>✅ 승인</button>
              <button
                className="drev-btn drev-danger"
                onClick={() => decide('reject')}
                disabled={busy}
              >🔁 수정 요청</button>
              <button className="drev-btn" onClick={() => decide('already')} disabled={busy} title="Slack으로 이미 답한 초안 — 결론은 Slack에 있고 여기서는 목록에서만 뺍니다">💬 이미 검토함</button>
              <button className="drev-btn" onClick={() => decide('skip')} disabled={busy} title="버그 화면녹화 등 초안이 아닌 영상">🚫 초안 아님</button>
            </div>

            <p className="drev-hint">
              ✉️ <b>승인</b>은 "확인했어요, 게시하세요" DM이 바로 갑니다(메모가 있으면 덧붙임). <b>수정 요청</b>은 메모나 장면별 피드백이 있을 때 DM이 갑니다.
              장면별 피드백이 있으면 두 경우 모두 리포트 링크(장면 캡처·시점·영어 문구)가 DM에 붙습니다.
              Slack에서 직접 답했다면 <b>리포트 링크 복사</b>로 붙여 넣고 <b>이미 검토함</b>을 눌러 주세요.
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default DraftReview;
